import $ from "jquery";
import "jquery-ui";
import "jquery-ui/ui/widgets/menu";
import "jquery.fancytree";
import "jquery.fancytree/dist/modules/jquery.fancytree.glyph";
import "jquery.fancytree/dist/modules/jquery.fancytree.wide";
import "jquery.fancytree/dist/modules/jquery.fancytree.dnd";
import "jquery.fancytree/dist/modules/jquery.fancytree.filter";
import "ui-contextmenu";

type ExtensionEntry = keyof Fancytree.Extensions.List | string;

export function fancytreeMergeExtensions(...extensionLists: (ExtensionEntry[] | undefined)[]): ExtensionEntry[] {
    const set = new Set<ExtensionEntry>();

    extensionLists.forEach(extensions => {
        if (Array.isArray(extensions)) {
            extensions.forEach(ext => set.add(ext));
        } else if (extensions) {
            set.add(extensions);
        }
    });

    return [...set];
}

export const fancytreeGlyphDefaults: Partial<Fancytree.FancytreeOptions> = {
    extensions: ["glyph"],
    glyph: {
        map: {
            doc: "lc-icon-Document",
            docOpen: "lc-icon-Document",
            error: "lc-icon-Alert",
            folder: "lc-icon-Folder",
            folderOpen: "lc-icon-Folder-open",
            loading: "lc-icon-Repeat",
            checkbox: "lc-icon-Checkbox-empty",
            checkboxUnknown: "lc-icon-Checkbox-indeterminate",
            checkboxSelected: "lc-icon-Checkbox-checked",
            expanderClosed: "lc-icon-Expand-alt",
            expanderOpen: "lc-icon-Expand",
            expanderLazy: "lc-icon-Expand",
            dragHelper: "",
            dropMarker: "",
            nodata: "",
            noExpander: "",
            radio: "",
            radioSelected: ""
        },
        // We just pick an existing preset to avoid a warning in console.
        // It doesn't matter as we override all values.
        preset: "bootstrap3"
    }
};

export const fancytreeCheckboxDefaults: Partial<Fancytree.FancytreeOptions> = {
    ...fancytreeGlyphDefaults,
    checkbox: true,
    selectMode: 2
};

export const fancytreeReadonlyDefaults: Partial<Fancytree.FancytreeOptions> = {
    ...fancytreeGlyphDefaults,
    extensions: fancytreeMergeExtensions(fancytreeGlyphDefaults.extensions, ["wide"]),
    checkbox: false,
    selectMode: 1
};

export const fancytreeReadonlyWideGlyphDefaults: Partial<Fancytree.FancytreeOptions> = {
    ...fancytreeGlyphDefaults,
    extensions: fancytreeMergeExtensions(fancytreeGlyphDefaults.extensions, ["wide"]),
    checkbox: false,
    selectMode: 1,
    wide: {
        iconWidth: "1em", // Adjust this if @fancy-icon-width != "16px"
        iconSpacing: "0.5em", // Adjust this if @fancy-icon-spacing != "3px"
        levelOfs: "1.5em", // Adjust this if ul padding != "16px"
        labelSpacing: "0em"
    }
};

export default class FancyTreeWrapper {
    myTree: Fancytree.Fancytree;
    lastSource: any;

    constructor(private readonly element: HTMLElement) {}

    destroy = () => {
        if (this.myTree) {
            $(this.element).fancytree("destroy");
            this.myTree = undefined;
        }
    };

    create = (options: Fancytree.FancytreeOptions) => {
        this.destroy(); // Just in case, won't do anything if nothing has been created before.

        this.lastSource = options.source;

        $(this.element).fancytree(options);

        if (options.extensions?.includes("wide") && this.element.classList.contains("lc-fancytree")) {
            const fancytreeClasses = $(this.element).fancytree("option", "_classNames");
            fancytreeClasses.animating = "lc-fancytree-animating";
            $(this.element).fancytree("option", "_classNames", fancytreeClasses);
        }

        this.myTree = $.ui.fancytree.getTree(this.element);
    };

    selectKey = (key: string) => {
        const node = this.myTree.activateKey(key);
        if (node) {
            node.scrollIntoView();
        }
    };

    selectSubNode = (key: string, subKey: string) => {
        const parentNode = this.myTree.getNodeByKey(key);

        if (parentNode) {
            const childArray = parentNode.children.filter(function (child) {
                return child.key === subKey;
            });
            if (childArray[0]) {
                childArray[0].setActive();
                childArray[0].scrollIntoView();
            }
        }
    };

    getActiveKey = () => {
        const activeNode = this.myTree.getActiveNode();
        if (activeNode) {
            return activeNode.key;
        }
        return undefined;
    };

    getActiveData = () => {
        const activeNode = this.myTree.getActiveNode();
        if (activeNode) {
            return activeNode.data;
        }
        return undefined;
    };

    refreshTree = () => {
        this.reload(this.lastSource);
    };

    updateTitle = (key: string, title: string) => {
        const node = this.myTree.getNodeByKey(key);
        if (node) {
            node.setTitle(title);
        }
    };

    reload = (source: any) => {
        const key = this.getActiveKey();
        this.lastSource = source;
        this.myTree.reload(source);
        if (key) {
            this.selectKey(key);
        }
    };

    getData = (key: string, isRecursive: boolean) => {
        const node = this.myTree.getNodeByKey(key);
        if (node) {
            return node.toDict(isRecursive);
        }
        return undefined;
    };

    updateData = (key: string, dict: any) => {
        const node = this.myTree.getNodeByKey(key);
        if (node) {
            // Ugly fix for properties moving to undefined, seem to be ignored otherwise.
            // At least when moving from 0 to undefined...
            for (const prop in dict.data) {
                if (dict.data[prop] === undefined) {
                    node.data[prop] = undefined;
                }
            }

            node.fromDict(dict);
            node.render();
        }
    };

    setContextMenu = (menu: any, updateMenuCallback: (node: Fancytree.FancytreeNode) => void) => {
        const options = {
            delegate: "span.fancytree-title",
            beforeOpen: (_event: JQueryEventObject, ui: any) => {
                const node = $.ui.fancytree.getNode(ui.target);

                // Activate node on right-click
                node.setActive();

                updateMenuCallback(node);
            },
            menu: menu,
            show: false,
            hide: false
        };

        ($(this.element) as any).contextmenu(options);
    };

    showCmd = (cmd: string) => {
        ($(this.element) as any).contextmenu("showEntry", cmd, true);
    };

    hideCmd = (cmd: string) => {
        ($(this.element) as any).contextmenu("showEntry", cmd, false);
    };

    expandAll = () => {
        // Expand all tree nodes
        this.myTree.visit(node => {
            node.setExpanded(true);
        });
    };

    collapseAll = () => {
        // Expand all tree nodes
        this.myTree.visit(node => {
            node.setExpanded(false);
        });
    };

    setSelectAll = (selected: boolean) => {
        // Expand all tree nodes
        this.myTree.visit(node => {
            node.setSelected(selected);
        });
    };

    expandRoot = () => {
        const tree = $.ui.fancytree.getTree(this.element);
        const rootNode = tree.getFirstChild();

        if (rootNode) {
            rootNode.setExpanded(true);
        }
    };

    collapseRoot = () => {
        const tree = $.ui.fancytree.getTree(this.element);
        const rootNode = tree.getFirstChild();

        if (rootNode) {
            rootNode.setExpanded(false);
        }
    };

    disableTree = () => {
        $(this.element).fancytree("disable");
    };

    disableTreeNode = (key: any) => {
        const node: Fancytree.FancytreeNode = this.myTree.getNodeByKey(key);
        this.setChildNodesUnselectable(node);
    };

    // There is no unselectable property for FancyTreeNodes as of now, hence need to use "any" for node
    setChildNodesUnselectable = (root: any) => {
        if (root !== null) {
            if (root.getChildren()) {
                const count: number = root.getChildren().length;
                for (let i = 0; i < count; i++) {
                    this.setChildNodesUnselectable(root.getChildren()[i]);
                }
            }
            root.unselectable = true;
        }
    };

    enableTree = () => {
        $(this.element).fancytree("option", "disabled", false);
    };
}
